import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { getTermScheduleExpanded } from '../../api/scheduleApi';
import { getTermWeeks } from '../../api/termApi';

const fmtTime = (t) => {
  if (!t) return '—';
  const [hh = '0', mm = '0'] = String(t).split(':');
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const m = parseInt(mm, 10);
  return m > 0 ? `${h12}:${String(m).padStart(2, '0')} ${ampm}` : `${h12}:00 ${ampm}`;
};

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'short',
  });

const fmtShortDate = (d) =>
  d ? new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

/**
 * Dated term timetable: expands the recurring weekly slots across the term's
 * weeks, with off (unavailable) weeks shown greyed out. Printable.
 */
const TermTimetableView = ({ terms }) => {
  const [termId, setTermId] = useState(terms[0]?.id ?? null);

  // terms arrives async from the lookups fetch — pick the first one when it lands.
  useEffect(() => {
    if (!termId && terms.length) setTermId(terms[0].id);
  }, [terms, termId]);
  const [weeks, setWeeks] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!termId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getTermWeeks(termId), getTermScheduleExpanded(termId)])
      .then(([weeksRes, expanded]) => {
        if (cancelled) return;
        // getTermWeeks returns { success, data: [...weeks], summary }
        setWeeks(weeksRes?.data || []);
        setOccurrences(expanded?.occurrences || []);
      })
      .catch(() => !cancelled && setError('Failed to load the term timetable.'))
      .finally(() => !cancelled && setLoading(false));
  }, [termId]);

  const termOptions = terms.map((t) => ({ label: t.name, value: t.id }));
  const byWeek = occurrences.reduce((acc, o) => {
    (acc[o.week_no] = acc[o.week_no] || []).push(o);
    return acc;
  }, {});

  return (
    <div className="term-timetable">
      <div className="flex align-items-center gap-2 mb-3 print-hidden">
        <Dropdown
          value={termId}
          options={termOptions}
          onChange={(e) => setTermId(e.value)}
          placeholder="Select Term"
          style={{ minWidth: 180 }}
        />
        <Button
          icon="pi pi-print"
          label="Print"
          className="p-button-outlined p-button-sm"
          onClick={() => window.print()}
          disabled={loading || !occurrences.length}
        />
        {!loading && (
          <span className="text-sm text-color-secondary ml-2">
            {weeks.filter((w) => w.is_available).length} available week{weeks.filter((w) => w.is_available).length !== 1 ? 's' : ''}
            {' · '}{occurrences.length} class occurrence{occurrences.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading && (
        <div className="text-center p-4 text-color-secondary">
          <i className="pi pi-spin pi-spinner mr-2" />Loading term timetable…
        </div>
      )}
      {error && <div className="p-3 text-center" style={{ color: 'var(--red-500)' }}>{error}</div>}

      {!loading && !error && weeks.map((week) => {
        const items = byWeek[week.week_no] || [];
        const range = `${fmtShortDate(week.week_start)} – ${fmtShortDate(week.week_end)}`;

        if (!week.is_available) {
          return (
            <div key={week.week_no} className="mb-2 p-2 border-round flex align-items-center gap-2"
              style={{ background: 'var(--surface-100)', opacity: 0.7 }}>
              <Tag value={`Week ${week.week_no}`} severity="secondary" />
              <span className="text-sm text-color-secondary">{range}</span>
              <Tag value="Off week — no classes" severity="warning" icon="pi pi-moon" />
            </div>
          );
        }

        // Group this week's occurrences by date
        const byDate = items.reduce((acc, o) => {
          (acc[o.date] = acc[o.date] || []).push(o);
          return acc;
        }, {});

        return (
          <div key={week.week_no} className="mb-3 border-1 surface-border border-round overflow-hidden">
            <div className="p-2 flex align-items-center gap-2" style={{ background: 'var(--surface-50)' }}>
              <Tag value={`Week ${week.week_no}`} />
              <span className="text-sm font-semibold">{range}</span>
              {items.length === 0 && (
                <span className="text-sm text-color-secondary">— no classes scheduled</span>
              )}
            </div>
            {Object.keys(byDate).sort().map((date) => (
              <div key={date} className="border-top-1 surface-border">
                <div className="px-3 py-1 text-sm font-semibold" style={{ background: 'var(--surface-0)' }}>
                  {fmtDate(date)}
                </div>
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <tbody>
                    {byDate[date].map((o, i) => (
                      <tr key={`${o.schedule_id}-${i}`} className="border-top-1 surface-border">
                        <td className="px-3 py-1" style={{ width: '11rem', whiteSpace: 'nowrap' }}>
                          {fmtTime(o.start_time)} – {fmtTime(o.end_time)}
                        </td>
                        <td className="px-3 py-1 font-semibold">{o.subject || '—'}</td>
                        <td className="px-3 py-1">{o.grade || '—'}</td>
                        <td className="px-3 py-1">{o.teacher || '—'}</td>
                        <td className="px-3 py-1 text-color-secondary">{o.classroom || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        );
      })}

      {!loading && !error && weeks.length === 0 && (
        <div className="p-4 text-center text-color-secondary">
          This term has no weeks defined yet. Set up its weeks in Sessions &amp; Terms first.
        </div>
      )}

      {/* Print: hide app chrome, keep only the timetable */}
      <style>{`
        @media print {
          .print-hidden, .layout-sidebar, .layout-topbar, .p-card > .p-card-body > .p-card-content > *:not(.term-timetable) { display: none !important; }
          body { background: #fff; }
        }
      `}</style>
    </div>
  );
};

export default TermTimetableView;
